import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Settings, Filter, Users, AlertTriangle, CheckCircle, Info } from 'lucide-react';

// Support types with descriptions
const SUPPORT_TYPE_LABELS = {
  'STANDARD': 'Standard Support',
  'HIGH_INTENSITY': 'High Intensity',
  'COMPLEX': 'Complex Support',
  'COMMUNITY_ACCESS': 'Community Access',
  'GROUP_ACTIVITIES': 'Group Activities',
  'SUPPORTED_EMPLOYMENT': 'Supported Employment',
  'SIL': 'Supported Independent Living',
  'RESPITE': 'Respite',
  'CENTRE_BASED': 'Centre-Based',
  'TRANSPORT': 'Transport',
  'OTHER': 'Other',
};

const ROUNDING_MODE_LABELS = {
  'UP': 'Round Up',
  'DOWN': 'Round Down',
  'NEAREST': 'Round to Nearest',
};

const VALIDATION_MODE_BADGES = {
  'warning': { label: 'Warning', variant: 'secondary', icon: Info },
  'blocking': { label: 'Blocking', variant: 'destructive', icon: AlertTriangle },
  'override_allowed': { label: 'Override Allowed', variant: 'outline', icon: CheckCircle },
};

export default function RatioRulesPage() {
  const { isAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Filters
  const [filterState, setFilterState] = useState('__all__');
  const [filterBranch, setFilterBranch] = useState('__all__');
  const [filterSupportType, setFilterSupportType] = useState('__all__');
  const [activeOnly, setActiveOnly] = useState(false);
  
  // Reference data
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supportTypes, setSupportTypes] = useState([]);
  const [roundingModes, setRoundingModes] = useState([]);
  const [validationModes, setValidationModes] = useState([]);
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    support_type: '',
    service_item_id: '',
    participants_per_staff: 4,
    minimum_staff: 1,
    rounding_mode: 'UP',
    validation_mode: 'warning',
    state_id: '',
    branch_id: '',
  });
  
  // Check if ratio engine is enabled (from feature flags)
  const [ratioEngineEnabled, setRatioEngineEnabled] = useState(false);

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // Load states/branches
        const [statesRes, branchesRes] = await Promise.allSettled([
          api.get('/org/states'),
          api.get('/org/branches'),
        ]);
        
        if (statesRes.status === 'fulfilled') {
          setStates(statesRes.value.data.data || []);
        }
        if (branchesRes.status === 'fulfilled') {
          setBranches(branchesRes.value.data.data || []);
        }
        
        // Load support types from API
        const typesRes = await api.get('/ratio-rules/support-types');
        if (typesRes.data?.data) {
          setSupportTypes(typesRes.data.data.support_types || []);
          setRoundingModes(typesRes.data.data.rounding_modes || []);
          setValidationModes(typesRes.data.data.validation_modes || []);
        }
        
        // Check feature flag
        try {
          const flagsRes = await api.get('/production/feature-flags');
          const flags = flagsRes.data?.data || {};
          setRatioEngineEnabled(flags.ratio_engine_enabled?.enabled || false);
        } catch {
          setRatioEngineEnabled(false);
        }
      } catch (error) {
        console.error('Failed to load reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  // Load ratio rules
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filterState && filterState !== '__all__') params.append('state_id', filterState);
      if (filterBranch && filterBranch !== '__all__') params.append('branch_id', filterBranch);
      if (filterSupportType && filterSupportType !== '__all__') params.append('support_type', filterSupportType);
      if (activeOnly) params.append('active_only', 'true');
      
      const res = await api.get(`/ratio-rules?${params.toString()}`);
      setRules(res.data.data.items || []);
      setTotal(res.data.data.total || 0);
    } catch (error) {
      console.error('Failed to load ratio rules:', error);
      toast.error('Failed to load ratio rules');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterState, filterBranch, filterSupportType, activeOnly]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        participants_per_staff: parseInt(formData.participants_per_staff),
        minimum_staff: parseInt(formData.minimum_staff),
        service_item_id: formData.service_item_id || null,
        state_id: formData.state_id || null,
        branch_id: formData.branch_id || null,
      };
      
      if (editingRule) {
        await api.put(`/ratio-rules/${editingRule.id}`, payload);
        toast.success('Ratio rule updated');
      } else {
        await api.post('/ratio-rules', payload);
        toast.success('Ratio rule created');
      }
      
      setShowCreateDialog(false);
      setEditingRule(null);
      resetForm();
      loadRules();
    } catch (error) {
      const message = error.response?.data?.detail?.error?.message || 'Failed to save ratio rule';
      toast.error(message);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (rule) => {
    try {
      await api.patch(`/ratio-rules/${rule.id}/status`, {
        active: !rule.active,
        reason: rule.active ? 'Deactivated by admin' : 'Activated by admin',
      });
      toast.success(rule.active ? 'Ratio rule deactivated' : 'Ratio rule activated');
      loadRules();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Open edit dialog
  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      support_type: rule.support_type,
      service_item_id: rule.service_item_id || '',
      participants_per_staff: rule.participants_per_staff,
      minimum_staff: rule.minimum_staff,
      rounding_mode: rule.rounding_mode,
      validation_mode: rule.validation_mode,
      state_id: rule.state_id || '',
      branch_id: rule.branch_id || '',
    });
    setShowCreateDialog(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      support_type: '',
      service_item_id: '',
      participants_per_staff: 4,
      minimum_staff: 1,
      rounding_mode: 'UP',
      validation_mode: 'warning',
      state_id: '',
      branch_id: '',
    });
  };

  // Check admin access
  if (!isAdmin) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <p className="text-lg font-medium">Access Denied</p>
            <p className="text-muted-foreground">You need admin access to manage ratio rules.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ratio-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" />
            Ratio Rules
          </h1>
          <p className="text-muted-foreground mt-1">
            Define staff-to-participant ratios for different support types
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!ratioEngineEnabled && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Ratio Engine Disabled
            </Badge>
          )}
          <Button onClick={() => { resetForm(); setEditingRule(null); setShowCreateDialog(true); }} data-testid="create-ratio-rule-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Feature Flag Warning */}
      {!ratioEngineEnabled && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800">Ratio Engine is Currently Disabled</p>
                <p className="text-sm text-orange-700 mt-1">
                  You can create and manage ratio rules, but they won't be applied during shift creation
                  until the feature flag is enabled. Go to <strong>Admin → Feature Flags</strong> to enable.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs">State</Label>
              <Select value={filterState} onValueChange={setFilterState}>
                <SelectTrigger data-testid="filter-state">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All States</SelectItem>
                  {states.map(s => (
                    <SelectItem key={s.state_id || s.id} value={s.state_id || s.id}>
                      {s.name || s.state_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Branch</Label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger data-testid="filter-branch">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Branches</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.branch_id || b.id} value={b.branch_id || b.id}>
                      {b.name || b.branch_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Support Type</Label>
              <Select value={filterSupportType} onValueChange={setFilterSupportType}>
                <SelectTrigger data-testid="filter-support-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Types</SelectItem>
                  {supportTypes.map(t => (
                    <SelectItem key={t} value={t}>{SUPPORT_TYPE_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="active-only"
                  checked={activeOnly}
                  onCheckedChange={setActiveOnly}
                  data-testid="filter-active-only"
                />
                <Label htmlFor="active-only" className="text-sm">Active Only</Label>
              </div>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setFilterState('__all__'); setFilterBranch('__all__'); setFilterSupportType('__all__'); setActiveOnly(false); }}>
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{total} Ratio Rules</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : rules.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Settings className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No ratio rules found</p>
              <p className="text-sm mt-1">Create your first ratio rule to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Support Type</TableHead>
                  <TableHead>Ratio</TableHead>
                  <TableHead>Min Staff</TableHead>
                  <TableHead>Rounding</TableHead>
                  <TableHead>Validation</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(rule => {
                  const ValidationIcon = VALIDATION_MODE_BADGES[rule.validation_mode]?.icon || Info;
                  return (
                    <TableRow key={rule.id} data-testid={`ratio-rule-row-${rule.id}`}>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{SUPPORT_TYPE_LABELS[rule.support_type] || rule.support_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono">1:{rule.participants_per_staff}</span>
                      </TableCell>
                      <TableCell>{rule.minimum_staff}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ROUNDING_MODE_LABELS[rule.rounding_mode] || rule.rounding_mode}
                      </TableCell>
                      <TableCell>
                        <Badge variant={VALIDATION_MODE_BADGES[rule.validation_mode]?.variant || 'secondary'}>
                          <ValidationIcon className="h-3 w-3 mr-1" />
                          {VALIDATION_MODE_BADGES[rule.validation_mode]?.label || rule.validation_mode}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {rule.state_id || 'Global'}
                        {rule.branch_id && <span className="text-muted-foreground"> / {rule.branch_id}</span>}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rule.active}
                          onCheckedChange={() => handleStatusToggle(rule)}
                          data-testid={`toggle-status-${rule.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(rule)} data-testid={`edit-rule-${rule.id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {page} of {Math.ceil(total / pageSize)}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit Ratio Rule' : 'Create Ratio Rule'}</DialogTitle>
              <DialogDescription>
                Define a staff-to-participant ratio for a specific support type.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="name">Rule Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Standard Group Support 1:4"
                  required
                  data-testid="input-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Support Type *</Label>
                  <Select value={formData.support_type} onValueChange={v => setFormData({...formData, support_type: v})} required>
                    <SelectTrigger data-testid="input-support-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {supportTypes.map(t => (
                        <SelectItem key={t} value={t}>{SUPPORT_TYPE_LABELS[t] || t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Validation Mode *</Label>
                  <Select value={formData.validation_mode} onValueChange={v => setFormData({...formData, validation_mode: v})}>
                    <SelectTrigger data-testid="input-validation-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warning">Warning (Advisory)</SelectItem>
                      <SelectItem value="blocking">Blocking (Hard Limit)</SelectItem>
                      <SelectItem value="override_allowed">Override Allowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Participants per Staff *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.participants_per_staff}
                    onChange={e => setFormData({...formData, participants_per_staff: e.target.value})}
                    required
                    data-testid="input-participants-per-staff"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Ratio = 1:{formData.participants_per_staff}</p>
                </div>
                <div>
                  <Label>Minimum Staff *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.minimum_staff}
                    onChange={e => setFormData({...formData, minimum_staff: e.target.value})}
                    required
                    data-testid="input-minimum-staff"
                  />
                </div>
                <div>
                  <Label>Rounding Mode</Label>
                  <Select value={formData.rounding_mode} onValueChange={v => setFormData({...formData, rounding_mode: v})}>
                    <SelectTrigger data-testid="input-rounding-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UP">Round Up</SelectItem>
                      <SelectItem value="DOWN">Round Down</SelectItem>
                      <SelectItem value="NEAREST">Round to Nearest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State (Scope)</Label>
                  <Select value={formData.state_id || '__none__'} onValueChange={v => setFormData({...formData, state_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger data-testid="input-state">
                      <SelectValue placeholder="Global (All States)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Global (All States)</SelectItem>
                      {states.map(s => (
                        <SelectItem key={s.state_id || s.id} value={s.state_id || s.id}>
                          {s.name || s.state_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Branch (Scope)</Label>
                  <Select value={formData.branch_id || '__none__'} onValueChange={v => setFormData({...formData, branch_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger data-testid="input-branch">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">All Branches</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.branch_id || b.id} value={b.branch_id || b.id}>
                          {b.name || b.branch_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-ratio-rule">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
